import { cn, formatAmount } from "@/lib/utils";
import { Currency } from "@/components/Currency";
import type { Goal } from "@/api/types";

/**
 * Курсовой эффект целевого фонда (SPEC-054).
 *
 * Без этой строки падение прогресса на 14% читается как ошибка учёта: пользователь
 * не откладывал меньше — просто валюта вклада ослабла к валюте цели. Показываем
 * три величины, из которых складывается разница: сколько внесено (в исходных
 * валютах, без конверсии), сколько это стоит сегодня и сколько было по курсам на
 * даты вкладов.
 *
 * Строка показывается в ОБОИХ режимах: во включённом объясняет, почему баланс
 * отличается от суммы вкладов, в выключенном — чем зафиксированный баланс
 * отличается от текущей стоимости.
 */

/** Копеечная разница — шум, а не сигнал: не мусорим ею карточку. */
const MIN_VISIBLE_EFFECT = 0.5;

/** Вклады в валюте, отличной от валюты цели — только из-за них и возникает эффект. */
function foreignContributions(goal: Goal): Goal["contributed_by_currency"] {
    return (goal.contributed_by_currency ?? []).filter(c => c.currency_code !== goal.target_currency);
}

/** «Вложено» — ВСЕ валюты вклада, а не только чужие: иначе у цели, куда шли и рубли,
 *  и доллары, строка «вложено 5 500 USDT» противоречит балансу в 2 100 000 ₽. */
function investedLabel(goal: Goal, compact = false): string {
    return (goal.contributed_by_currency ?? [])
        .map(c => `${compact ? compactAmount(c.amount) : formatAmount(c.amount, c.currency_code)} ${c.currency_code}`)
        .join(" + ");
}

/** Целые суммы в узкой строке карточки — без копеечного хвоста: «1 600 000 RUB»
 *  читается с одного взгляда, «1 600 000,00 RUB» вытесняет за край сам эффект. */
function compactAmount(amount: number): string {
    return Number.isInteger(amount)
        ? amount.toLocaleString("ru-RU", { maximumFractionDigits: 0 })
        : amount.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Есть ли что показывать: нужны вклады в валюте, отличной от валюты цели. */
export function hasFxEffect(goal: Goal): boolean {
    if (!goal.target_currency) return false;
    if (!foreignContributions(goal).length) return false;
    // Неполная оценка — тоже сигнал (предупреждение вместо дельты).
    if (goal.fx_effect == null) return true;
    return Math.abs(goal.fx_effect) >= MIN_VISIBLE_EFFECT;
}

export function GoalFxEffect({ goal, variant = "inline", className }: {
    goal: Goal;
    /** inline — одна строка под балансом (карточка); block — раскладка величин (detail). */
    variant?: "inline" | "block";
    className?: string;
}) {
    if (!hasFxEffect(goal)) return null;
    const ccy = goal.target_currency!;
    const invested = investedLabel(goal);

    // Оценка неполна — разница двух неполных сумм не является курсовым эффектом.
    if (goal.fx_effect == null) {
        const missing = Math.max(goal.mark_to_market_missing_rates, goal.fixed_missing_rates);
        return (
            <div className={cn("text-xs text-amber-600 dark:text-amber-400 truncate", className)}
                 title={`Вложено ${invested}; ${missing} вкладов без курса — точную разницу посчитать нельзя`}>
                курсовой эффект не посчитать: {missing} без курса · вложено {investedLabel(goal, true)}
            </div>
        );
    }

    const positive = goal.fx_effect > 0;
    const sign = positive ? "+" : "−";
    const effect = `${sign}${formatAmount(Math.abs(goal.fx_effect), ccy)}`;
    const effectClass = positive ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400";

    if (variant === "inline") {
        return (
            <div
                className={cn("text-xs text-muted-foreground tabular-nums truncate", className)}
                title={`Вложено ${invested} · сегодня ${formatAmount(goal.balance_mark_to_market, ccy)} ${ccy} · по курсам на даты вкладов ${formatAmount(goal.balance_fixed, ccy)} ${ccy}`}
            >
                курсовой эффект <span className={cn("font-medium", effectClass)}>{effect} {ccy}</span>
                {" · "}вложено {investedLabel(goal, true)}
            </div>
        );
    }

    return (
        <div className={cn("rounded-xl border bg-card p-4 space-y-2 text-sm", className)}>
            <div className="text-muted-foreground text-xs">
                {goal.valuation_mode === "mark_to_market"
                    ? "Баланс считается по текущему курсу валюты вклада"
                    : "Баланс зафиксирован по курсам на даты вкладов"}
            </div>
            <Row label="Вложено" value={invested} />
            <Row
                label="Сегодня"
                value={<><span className="num tabular-nums">{formatAmount(goal.balance_mark_to_market, ccy)}</span> <Currency code={ccy} size="xs" /></>}
            />
            <Row
                label="По курсам вкладов"
                value={<><span className="num tabular-nums">{formatAmount(goal.balance_fixed, ccy)}</span> <Currency code={ccy} size="xs" /></>}
            />
            <Row
                label="Курсовой эффект"
                value={<span className={cn("num tabular-nums font-medium", effectClass)}>{effect} {ccy}</span>}
            />
        </div>
    );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div className="flex items-baseline justify-between gap-4">
            <span className="text-muted-foreground">{label}</span>
            <span>{value}</span>
        </div>
    );
}
